import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Apple, BarChart3, CalendarRange, LineChart, Sprout, Target } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Tabs } from '../../components/ui/Tabs';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import harvestService from '../../services/harvest';
import insightsService from '../../services/insights';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { inputClass } from '../../components/fieldWork/FormModal';
import { Notice } from '../../components/labour/shared';
import OverviewPanel from '../../components/insights/OverviewPanel';
import BudgetPanel from '../../components/insights/BudgetPanel';
import HarvestProgressPanel from '../../components/insights/HarvestProgressPanel';
import HarvestDatesPanel from '../../components/insights/HarvestDatesPanel';
import YieldHistoryPanel from '../../components/insights/YieldHistoryPanel';
import { seasonLabel, type Insights as InsightsData } from '../../types/insights';

type TabKey = 'overview' | 'budget' | 'harvest' | 'dates' | 'yield';
const TABS: { key: TabKey; label: string; icon: ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'budget', label: 'Budget', icon: <Target className="h-4 w-4" /> },
  { key: 'harvest', label: 'Harvest progress', icon: <Apple className="h-4 w-4" /> },
  { key: 'dates', label: 'Harvest dates', icon: <CalendarRange className="h-4 w-4" /> },
  { key: 'yield', label: 'Yield history', icon: <Sprout className="h-4 w-4" /> },
];

/**
 * /jobs/insights — the season at a glance: per-ha and per-bin figures, budget
 * vs actual with a forecast, and harvest progress against the crop estimate.
 * Gated in App.tsx on a farm_jobs supervisor / creator / admin seat; dollar
 * figures and budget editing are admin-only (the server strips money too),
 * crop estimates need creator or admin.
 */
export default function Insights() {
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some(t => t.key === requested) ? (requested as TabKey) : 'overview';
  const { hasAccess: isAdmin } = useModuleLicense('farm_jobs', 'admin');
  const { hasAccess: canEstimate } = useModuleLicense('farm_jobs', ['creator', 'admin']);

  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [overview, setOverview] = useState<InsightsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    harvestService.getSettings()
      .then(s => setStartMonth(s.season_start_month || 7))
      .catch(() => setStartMonth(7));
    orchardService.getAllFarms().then(r => { if (r.success) setFarms(r.data.farms.map(f => ({ id: f.id, name: f.name }))); }).catch(() => {});
  }, []);

  // The current season, from the org's calendar and season start month.
  const today = getTodayInTz(tz);
  const current = startMonth == null ? null
    : (Number(today.slice(5, 7)) >= startMonth ? Number(today.slice(0, 4)) : Number(today.slice(0, 4)) - 1);
  const shown = season ?? current;

  useEffect(() => {
    if (tab !== 'overview' || shown == null) return;
    let live = true;
    setOverview(null);
    insightsService.getInsights({ season: shown, farm_id: farmId ?? undefined })
      .then(d => { if (live) { setOverview(d); setError(null); } })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load insights')); });
    return () => { live = false; };
  }, [tab, shown, farmId]);

  const name = (y: number) => seasonLabel(y, startMonth ?? 7);
  const seasons = current == null ? [] : [current + 1, current, current - 1, current - 2, current - 3];

  return (
    <>
      <PageHeader title="Insights" subtitle="Season figures, budget and harvest progress" icon={<LineChart className="h-6 w-6 text-white" />} />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-xs text-neutral-600">
            <span className="block mb-1">Season</span>
            <select className={inputClass} value={shown ?? ''} onChange={e => setSeason(Number(e.target.value))} aria-label="Season">
              {seasons.map(y => <option key={y} value={y}>{name(y)}{y === current ? ' (this season)' : ''}</option>)}
            </select>
          </label>
          <label className="text-xs text-neutral-600 min-w-0">
            <span className="block mb-1">Farm</span>
            <select className={inputClass} value={farmId ?? ''} onChange={e => setFarmId(e.target.value ? Number(e.target.value) : null)} aria-label="Farm">
              <option value="">All farms</option>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
        </div>
        <Tabs tabs={TABS} active={tab} onChange={k => setParams(k === 'overview' ? {} : { tab: k }, { replace: true })} className="mb-4" />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {shown == null ? <p className="text-sm text-neutral-500 py-6 text-center">Loading…</p> : (
            <>
              {tab === 'overview' && (error ? <Notice tone="error">{error}</Notice>
                : overview ? <OverviewPanel data={overview} /> : <p className="text-sm text-neutral-500 py-6 text-center">Loading…</p>)}
              {tab === 'budget' && <BudgetPanel season={shown} farmId={farmId} canEdit={isAdmin} seasonName={name} />}
              {tab === 'harvest' && <HarvestProgressPanel season={shown} farmId={farmId} canEdit={canEstimate} />}
              {tab === 'dates' && <HarvestDatesPanel season={shown} farmName={farms.find(f => f.id === farmId)?.name ?? null} canPlan={canEstimate} />}
              {tab === 'yield' && <YieldHistoryPanel farmId={farmId} seasonName={name} />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
